import { Link } from "react-router";
import { ExternalLink, Loader2, RotateCcw } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import type { PublicEchoFeedbackRecord } from "@/server/echoFeedback";
import {
  formatDateTime,
  formatVideoTimestamp,
} from "../echoflicks/sessionUtils";
import { FeedbackCategoryChips } from "./FeedbackCategoryChips";
import {
  FeedbackMetadataStatusBadge,
  FeedbackNotificationStatusBadge,
} from "./FeedbackStatusBadges";
import { buildFeedbackEchoHref } from "./feedbackDeepLink";
import { feedbackReporterLabel } from "./feedbackRecordView";
import { useFeedbackFrame } from "./useFeedbackFrame";
import { feedbackCoverage } from "./feedbackCoverage";

export const FeedbackDetailsDialog = ({
  record,
  retrying,
  onRetry,
  onOpenChange,
}: {
  record: PublicEchoFeedbackRecord | null;
  retrying: boolean;
  onRetry: (record: PublicEchoFeedbackRecord) => void;
  onOpenChange: (open: boolean) => void;
}) => {
  const frame = useFeedbackFrame(record?.id, Boolean(record?.hasFrame));
  return (
    <Dialog open={Boolean(record)} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-5xl">
        {record ? (
          <>
            <DialogHeader>
              <DialogTitle>
                {record.echo.name} —{" "}
                {formatVideoTimestamp(record.position.timestampMs / 1000)}
              </DialogTitle>
              <DialogDescription>
                Captured at submission by {feedbackReporterLabel(record)} on{" "}
                {formatDateTime(record.submittedAt)}. This is the saved
                historical snapshot, not a re-resolution of current Echo state.
              </DialogDescription>
            </DialogHeader>

            <div className="flex flex-wrap items-center gap-2">
              <FeedbackCategoryChips record={record} />
              <FeedbackMetadataStatusBadge status={record.metadata.status} />
              <FeedbackNotificationStatusBadge
                status={record.notification.status}
              />
            </div>

            {record.hasFrame ? (
              <div className="overflow-hidden rounded-md border bg-black">
                {frame.status === "ready" ? (
                  <img
                    src={frame.url}
                    alt={`Frame at ${formatVideoTimestamp(record.position.timestampMs / 1000)}`}
                    className="max-h-[55vh] w-full object-contain"
                  />
                ) : frame.status === "error" ? (
                  <div className="flex aspect-video items-center justify-center p-4 text-sm text-white/70">
                    {frame.error}
                  </div>
                ) : (
                  <div className="flex aspect-video items-center justify-center gap-2 text-sm text-white/70">
                    <Loader2 className="size-4 animate-spin" />
                    Loading private frame...
                  </div>
                )}
              </div>
            ) : null}

            <div className="rounded-md border bg-muted/30 p-3">
              <div className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Reviewer comment
              </div>
              <p className="whitespace-pre-wrap text-sm">{record.comment}</p>
            </div>

            <FeedbackMetadataPanel record={record} />

            <div className="flex flex-wrap items-center justify-between gap-2 border-t pt-3">
              <div className="text-xs text-muted-foreground">
                Feedback ID: <code>{record.clientRequestId}</code> ·{" "}
                {record.schemaVersion} · {record.taxonomyVersion}
              </div>
              <div className="flex gap-2">
                <Button asChild size="sm">
                  <Link
                    to={buildFeedbackEchoHref(record).replace(
                      "#echo-final-video",
                      `&felixFeedbackId=${encodeURIComponent(record.clientRequestId)}#echo-final-video`,
                    )}
                  >
                    Ask Felix about this feedback
                  </Link>
                </Button>
                {record.notification.status !== "sent" ? (
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={retrying}
                    onClick={() => onRetry(record)}
                  >
                    {retrying ? (
                      <Loader2 className="size-4 animate-spin" />
                    ) : (
                      <RotateCcw className="size-4" />
                    )}
                    Retry Slack alert
                  </Button>
                ) : null}
                <Button asChild variant="outline" size="sm">
                  <Link to={buildFeedbackEchoHref(record)}>
                    Open Echo
                    <ExternalLink className="size-4" />
                  </Link>
                </Button>
              </div>
            </div>
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

const FeedbackMetadataPanel = ({
  record,
}: {
  record: PublicEchoFeedbackRecord;
}) => {
  const sections = [
    ["Core", record.metadata.core],
    ["Timeline & scene", record.metadata.timeline],
    ["I2V / animation", record.metadata.i2v],
    ["Media & framing", record.metadata.media],
    ["Audio & timing", record.metadata.audio],
    ["Configuration provenance", record.metadata.configuration],
  ] as const;
  return (
    <div className="space-y-3">
      <div>
        <h3 className="text-sm font-semibold">Diagnostic metadata</h3>
        <p className="text-xs text-muted-foreground">
          Schema {record.metadataSchemaVersion} · captured{" "}
          {formatDateTime(record.metadata.capturedAt)}
        </p>
      </div>
      <div className="grid gap-3 lg:grid-cols-2">
        {sections.map(([title, value]) => (
          <FeedbackMetadataSection key={title} title={title} value={value} />
        ))}
      </div>
      <FeedbackMetadataSection
        title="Evidence coverage (current interpretation)"
        value={feedbackCoverage(record.metadata)}
      />
      <FeedbackMetadataSection
        title="Collector status"
        value={record.metadata.collectors}
      />
    </div>
  );
};

const FeedbackMetadataSection = ({
  title,
  value,
}: {
  title: string;
  value: unknown;
}) => (
  <section className="min-w-0 rounded-md border">
    <h4 className="border-b bg-muted/40 px-3 py-2 text-sm font-medium">
      {title}
    </h4>
    {hasContent(value) ? (
      <pre className="max-h-80 overflow-auto whitespace-pre-wrap break-words p-3 text-xs">
        {JSON.stringify(value, null, 2)}
      </pre>
    ) : (
      <p className="p-3 text-xs text-muted-foreground">
        Unavailable for this report.
      </p>
    )}
  </section>
);

function hasContent(value: unknown) {
  if (value == null) return false;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "object") return Object.keys(value).length > 0;
  return true;
}
